import React from 'react'
import { useTranslation } from 'react-i18next'
import { BaseTextProps } from './types'
import { useTheme } from '@/hooks/useTheme'
import { responsiveValueAdapter } from '@/utils'
import { Skeleton } from '../../Skeleton'
import { BaseView } from '../BaseView'

export const BaseText: React.FC<BaseTextProps> = ({
  color = 'label.default',
  fontSize = 16,
  fontFamily = 'regular',
  textAlign,
  textTransform,
  italic,
  lineHeight,
  letterSpacing,
  decoration,

  translationKey,
  style,
  children,
  skeletonActive,
  skeletonWidth = 200,
  numberOfLines,
  className,
  ...rest
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  // Resolve color from theme
  const getTextColor = (colorPath: string): string => {
    // Проверяем, что theme.colors существует
    if (!theme.colors) {
      console.warn('BaseText: theme.colors is undefined')
      return '#ffffff' // Fallback цвет
    }

    if (colorPath.includes('.')) {
      const keys = colorPath.split('.')
      let value: any = theme.colors

      for (const key of keys) {
        if (value && typeof value === 'object') {
          value = value[key as keyof typeof value]
        } else {
          // Если путь не найден, возвращаем дефолтный цвет
          console.warn(
            `BaseText: Color path "${colorPath}" not found in theme, key "${key}" is missing`,
          )
          return theme.colors?.label?.default || '#ffffff'
        }
      }

      const result =
        (value as string) || theme.colors?.label?.default || '#ffffff'
      return result
    }
    return colorPath
  }

  // Responsive font size
  const responsiveFontSize = responsiveValueAdapter(fontSize) || fontSize

  if (skeletonActive) {
    return (
      <BaseView
        style={{ alignSelf: textAlign === 'center' ? 'center' : undefined }}
      >
        <Skeleton
          viewBox={`0 0 ${skeletonWidth} ${responsiveFontSize}`}
          style={{
            width: skeletonWidth,
            height: responsiveFontSize,
          }}
        >
          <rect
            rx="4"
            ry="4"
            width={`${skeletonWidth}`}
            height={`${responsiveFontSize}`}
          />
        </Skeleton>
      </BaseView>
    )
  }

  // Создаем Tailwind классы для базовых стилей
  const getTailwindClasses = (): string => {
    const classes: string[] = []

    // Font weight mapping
    switch (fontFamily) {
      case 'light':
        classes.push('font-light') // font-weight: 300
        break
      case 'regular':
        classes.push('font-normal') // font-weight: 400
        break
      case 'medium':
        classes.push('font-medium') // font-weight: 500
        break
      case 'semibold':
        classes.push('font-semibold') // font-weight: 600
        break
      case 'bold':
        classes.push('font-bold') // font-weight: 700
        break
      default:
        classes.push('font-normal')
        break
    }

    // Text align
    if (textAlign === 'center') classes.push('text-center')
    else if (textAlign === 'right') classes.push('text-right')
    else if (textAlign === 'left') classes.push('text-left')
    else if (textAlign === 'justify') classes.push('text-justify')

    // Text transform
    if (textTransform === 'uppercase') classes.push('uppercase')
    else if (textTransform === 'lowercase') classes.push('lowercase')
    else if (textTransform === 'capitalize') classes.push('capitalize')

    // Font style
    if (italic) classes.push('italic')

    // Text decoration
    if (decoration === 'underline') classes.push('underline')
    else if (decoration === 'line-through') classes.push('line-through')
    else if (decoration === 'none') classes.push('no-underline')

    return classes.join(' ')
  }

  // Стили, которые не могут быть заменены Tailwind классами
  const inlineStyles: React.CSSProperties = {
    // Color (всегда используем theme цвета)
    color: getTextColor(color),

    // Font size (используем responsive значение)
    fontSize: `${responsiveFontSize}px`,

    // Font family (используем системный шрифт)
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',

    // Line height (если задан)
    ...(lineHeight && { lineHeight: `${lineHeight}px` }),

    // Letter spacing (если задан)
    ...(letterSpacing && { letterSpacing: `${letterSpacing}px` }),

    // Text truncation support like React Native numberOfLines
    ...(numberOfLines && {
      display: '-webkit-box',
      WebkitLineClamp: numberOfLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      // For single line, also add white-space: nowrap for better compatibility
      ...(numberOfLines === 1 && { whiteSpace: 'nowrap', display: 'block' }),
    }),

    // Custom styles override
    ...style,
  }

  const combinedClassName = [getTailwindClasses(), className]
    .filter(Boolean)
    .join(' ')

  return (
    <span style={inlineStyles} className={combinedClassName} {...rest}>
      {translationKey ? t(translationKey) : children}
    </span>
  )
}
